import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    collection,
    onSnapshot,
    query,
    orderBy,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './BizMessages.css';

interface BizMessageSummary {
    id: string;
    displayName: string;
    email?: string;
    lastMessage: string;
    lastMessageAt: Timestamp;
    status: 'open' | 'closed';
    unreadByAdmin: boolean;
}

export const BizMessages: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    const [threads, setThreads] = useState<BizMessageSummary[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const q = query(
            collection(db, 'bizMessages'),
            orderBy('lastMessageAt', 'desc')
        );
        const unsub = onSnapshot(q, (snap) => {
            const loaded: BizMessageSummary[] = snap.docs.map((d) => ({
                id: d.id,
                ...(d.data() as Omit<BizMessageSummary, 'id'>),
            }));
            setThreads(loaded);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (err) {
            console.error('Sign out failed:', err);
        }
    };

    const formatRelative = (ts: Timestamp | undefined) => {
        if (!ts?.toDate) return '';
        const diff = Date.now() - ts.toDate().getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    };

    return (
        <div className="biz-msgs-container">
            <header className="biz-msgs-header">
                <div className="biz-msgs-header-content">
                    <div>
                        <h1>💼 Biz Messages</h1>
                        <p className="biz-msgs-subtitle">Messages from biz.omore.app users</p>
                    </div>
                    <div className="biz-msgs-header-actions">
                        <button onClick={() => navigate('/dashboard')} className="biz-msgs-back-button">
                            ← Back to Dashboard
                        </button>
                        <span className="biz-msgs-user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="biz-msgs-logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="biz-msgs-main">
                {loading ? (
                    <div className="biz-msgs-loading">
                        <div className="biz-msgs-spinner"></div>
                        <p>Loading messages...</p>
                    </div>
                ) : threads.length === 0 ? (
                    <div className="biz-msgs-empty">
                        <div className="biz-msgs-empty-icon">💼</div>
                        <h3>No messages yet</h3>
                        <p>Messages from biz.omore.app users will appear here.</p>
                    </div>
                ) : (
                    <div className="biz-msgs-list">
                        {threads.map((thread) => (
                            <div
                                key={thread.id}
                                className={`biz-msg-row ${thread.unreadByAdmin ? 'unread' : ''}`}
                                onClick={() => navigate(`/biz-messages/${thread.id}`)}
                            >
                                <div className="biz-msg-row-left">
                                    {thread.unreadByAdmin && (
                                        <span className="biz-unread-dot" title="Unread"></span>
                                    )}
                                    <div className="biz-msg-row-info">
                                        <span className="biz-msg-display-name">{thread.displayName}</span>
                                        {thread.email && (
                                            <span className="biz-msg-email">{thread.email}</span>
                                        )}
                                        <span className="biz-msg-preview">
                                            {thread.lastMessage.length > 80
                                                ? `${thread.lastMessage.substring(0, 80)}...`
                                                : thread.lastMessage}
                                        </span>
                                    </div>
                                </div>
                                <div className="biz-msg-row-right">
                                    <span className="biz-msg-time">
                                        {formatRelative(thread.lastMessageAt)}
                                    </span>
                                    <span className={`biz-status-badge biz-status-${thread.status}`}>
                                        {thread.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default BizMessages;
